import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit, Eye, Trash2, Loader2, Sparkles, Save, X, EyeOff, AlertCircle, Download, CheckCircle2, Image as ImageIcon, Upload, Bold, Italic, Heading2, Heading3, List, Link as LinkIcon, Quote, FileText } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import AppHeader from "../components/AppHeader";

const CATEGORIES = ["Conceptos básicos", "Cargas y deudas", "Procesos críticos", "Post-subasta"];
const CTAS = [
  { value: "orientacion", label: "Orientación 15 €" },
  { value: "consultoria", label: "Consultoría 95 €" },
  { value: "informe_premium", label: "Informe Premium" },
];
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminBlog() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog/posts");
      setPosts(data.posts || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchPosts(); }, []);

  const togglePublish = async (p) => {
    await api.patch(`/admin/blog/posts/${p.id}`, { published: !p.published });
    fetchPosts();
  };
  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"?`)) return;
    await api.delete(`/admin/blog/posts/${p.id}`);
    fetchPosts();
  };
  const openEditor = async (p) => {
    if (!p) { setEditing({ _new: true }); return; }
    const { data } = await api.get(`/admin/blog/posts/${p.id}`);
    setEditing(data);
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <AppHeader />
      <header className="px-6 py-5 bg-stone-900 border-b border-stone-800">
        <div className="max-w-[1400px] mx-auto flex items-center gap-4">
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F]">Admin · Blog</div>
            <h1 className="font-display text-3xl font-black tracking-tight text-white">Artículos</h1>
            <div className="text-sm text-stone-400 mt-1">{posts.length} artículo(s) · Edita, crea y publica desde aquí.</div>
          </div>
          <button onClick={() => openEditor(null)} className="inline-flex items-center gap-1.5 bg-stone-800 hover:bg-stone-700 border border-stone-700 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="blog-new-btn" title="Crear artículo manualmente rellenando título, contenido y SEO">
            <Plus className="w-4 h-4" /> Artículo manual
          </button>
          <AutoGenerateNowButton onDone={fetchPosts} />
          <ImportSeedButton onDone={fetchPosts} />
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#E0651F] mx-auto" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-stone-500">Sin artículos. Crea el primero o usa el script seed.</div>
        ) : (
          <div className="bg-stone-900 border border-stone-800 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-800/60 text-stone-400 text-[10px] uppercase tracking-[0.15em] font-bold">
                <tr>
                  <th className="text-left px-4 py-3">Título</th>
                  <th className="text-left px-4 py-3">Categoría</th>
                  <th className="text-left px-4 py-3">CTA</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-left px-4 py-3">Vistas</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-800">
                {posts.map((p) => (
                  <tr key={p.id} className="hover:bg-stone-800/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.cover_image_url ? (
                          <img src={p.cover_image_url.startsWith("http") ? p.cover_image_url : `${API_BASE}${p.cover_image_url}`} alt="" className="w-14 h-10 object-cover rounded-sm flex-shrink-0" />
                        ) : (
                          <span className="text-2xl flex-shrink-0 w-14 text-center">{p.cover_emoji || "📄"}</span>
                        )}
                        <div className="min-w-0">
                          <div className="font-bold text-white flex items-center gap-2">
                            {p.title}
                            {p.auto_generated && <span className="text-[9px] tracking-wide uppercase font-bold text-amber-400 bg-amber-900/30 px-1.5 py-0.5 rounded-sm">Auto</span>}
                          </div>
                          <div className="text-[11px] text-stone-500 mt-0.5">/{p.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">{p.category}</td>
                    <td className="px-4 py-3 text-xs uppercase">{p.cta_target}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => togglePublish(p)} className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wide font-bold rounded-sm ${p.published ? "bg-emerald-900/40 text-emerald-300" : "bg-stone-700 text-stone-400"}`}>
                        {p.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {p.published ? "Publicado" : "Borrador"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-400">{p.views || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-white p-1.5" title="Ver"><Eye className="w-4 h-4" /></a>
                        <button onClick={() => openEditor(p)} className="text-stone-400 hover:text-white p-1.5" title="Editar"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => remove(p)} className="text-stone-500 hover:text-red-400 p-1.5" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {editing && <PostEditor post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchPosts(); }} />}
    </div>
  );
}

function PostEditor({ post, onClose, onSaved }) {
  const isNew = !!post._new;
  const [form, setForm] = useState({
    title: post.title || "",
    excerpt: post.excerpt || "",
    content_markdown: post.content_markdown || "",
    category: post.category || "Conceptos básicos",
    cta_target: post.cta_target || "orientacion",
    keywords: (post.keywords || []).join(", "),
    cover_emoji: post.cover_emoji || "📄",
    cover_image_url: post.cover_image_url || "",
    cover_image_alt: post.cover_image_alt || "",
    meta_description: post.meta_description || "",
    published: post.published !== false,
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiNotes, setAiNotes] = useState("");
  const [aiCluster, setAiCluster] = useState("");
  const [seoPlan, setSeoPlan] = useState([]);
  const [seoPlanSel, setSeoPlanSel] = useState("");
  const [err, setErr] = useState("");
  const [generatingCover, setGeneratingCover] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [copyingPrompt, setCopyingPrompt] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  const copyChatGptPrompt = async () => {
    setCopyingPrompt(true); setPromptCopied(false); setErr("");
    try {
      const { data } = await api.get("/admin/blog/next-seo-topic");
      // Autocompleta título/categoría/keywords con el siguiente tema
      setForm(f => ({
        ...f,
        title: data.title || f.title,
        category: data.category || f.category,
        keywords: (data.keywords && data.keywords.length ? data.keywords.join(", ") : f.keywords),
      }));
      setAiCluster(data.cluster || "");
      setAiNotes(data.notes || "");
      // Copia el prompt al portapapeles
      await navigator.clipboard.writeText(data.prompt || "");
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 8000);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el siguiente tema SEO");
    } finally {
      setCopyingPrompt(false);
    }
  };
  const textareaRef = useRef(null);
  const coverInputRef = useRef(null);
  const inlineInputRef = useRef(null);

  // Cargar el plan SEO una vez (solo al crear nuevo artículo)
  useEffect(() => {
    if (!isNew) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/blog/seo-plan");
        if (!cancel) setSeoPlan(data?.plan || []);
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancel = true; };
  }, [isNew]);

  const loadFromPlan = (slug) => {
    setSeoPlanSel(slug);
    if (!slug) return;
    const item = seoPlan.find((p) => p.slug === slug);
    if (!item) return;
    setForm((f) => ({
      ...f,
      title: item.title,
      category: item.category_resolved || f.category,
      keywords: (item.keywords || []).join(", "),
    }));
    setAiNotes(item.notes || "");
    setAiCluster(item.cluster || "");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const generateDraft = async () => {
    if (!form.title || form.title.length < 10) { setErr("Pon un título antes de generar"); return; }
    setGenerating(true); setErr("");
    try {
      // Usa la versión async para evitar Cloudflare 524 (Claude tarda 30-90s)
      const { data: job } = await api.post("/admin/blog/draft-async", {
        title: form.title,
        category: form.category,
        cta_target: form.cta_target,
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        notes: aiNotes,
        cluster: aiCluster,
      });
      // Poll hasta que termine (máx 3 min) — tolera errores transitorios de red
      const maxAttempts = 60;
      let attempt = 0;
      let consecutiveFails = 0;
      while (attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempt++;
        try {
          const { data: s } = await api.get(`/admin/blog/draft-jobs/${job.job_id}`);
          consecutiveFails = 0;  // reset on success
          if (s.status === "done") {
            const r = s.result || {};
            setForm({ ...form, content_markdown: r.content_markdown, excerpt: r.excerpt, meta_description: r.meta_description || form.meta_description });
            return;
          }
          if (s.status === "error") {
            setErr(formatApiError(s.error) || "Error generando con IA");
            return;
          }
        } catch (netErr) {
          // Cloudflare 502/504 transitorio durante Claude — reintentamos
          consecutiveFails++;
          if (consecutiveFails >= 5) {
            setErr("Error de red persistente durante la generación. Refresca y reintenta.");
            return;
          }
          // continue polling
        }
      }
      setErr("Tiempo de espera agotado (>3 min). Inténtalo de nuevo.");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error generando con IA");
    } finally {
      setGenerating(false);
    }
  };

  const generateCover = async () => {
    if (!form.title || form.title.length < 10) { setErr("Pon un título de al menos 10 caracteres antes de generar la imagen"); return; }
    setGeneratingCover(true); setErr("");
    try {
      // Bump variation on each click so "Regenerate" gives a genuinely different visual
      const currentVar = Number(form._coverVariation || 0);
      const { data } = await api.post("/admin/blog/generate-cover", {
        title: form.title,
        category: form.category,
        style: "profesional",
        extra_prompt: "",
        variation: currentVar,
      });
      setForm((f) => ({
        ...f,
        cover_image_url: data.url,
        cover_image_alt: f.cover_image_alt || form.title,
        _coverVariation: currentVar + 1,
      }));
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error generando imagen con IA");
    } finally {
      setGeneratingCover(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingImg(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/blog/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image_url: data.url, cover_image_alt: f.cover_image_alt || form.title }));
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error subiendo imagen");
    } finally {
      setUploadingImg(false);
    }
  };

  const uploadInline = async (file) => {
    if (!file) return;
    setUploadingImg(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/blog/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const alt = form.title || "imagen";
      const md = `\n\n![${alt}](${data.url})\n\n`;
      insertAtCursor(md);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error subiendo imagen");
    } finally {
      setUploadingImg(false);
    }
  };

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) {
      setForm((f) => ({ ...f, content_markdown: f.content_markdown + text }));
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = form.content_markdown.slice(0, start);
    const after = form.content_markdown.slice(end);
    const newVal = before + text + after;
    setForm((f) => ({ ...f, content_markdown: newVal }));
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + text.length;
    });
  };

  const wrapSelection = (left, right = left) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = form.content_markdown.slice(start, end);
    const replacement = `${left}${selected || "texto"}${right}`;
    insertAtCursor(replacement);
  };

  const insertLine = (prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    // Avanza al inicio de la línea actual
    const before = form.content_markdown.slice(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const newVal = form.content_markdown.slice(0, lineStart) + prefix + form.content_markdown.slice(lineStart);
    setForm((f) => ({ ...f, content_markdown: newVal }));
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + prefix.length;
    });
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = {
        ...form,
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) await api.post("/admin/blog/posts", payload);
      else await api.patch(`/admin/blog/posts/${post.id}`, payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e?.response?.data?.detail) || "Error guardando"); }
    finally { setSaving(false); }
  };

  const coverPreviewUrl = form.cover_image_url
    ? (form.cover_image_url.startsWith("http") ? form.cover_image_url : `${API_BASE}${form.cover_image_url}`)
    : null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-stone-900 border border-stone-700 max-w-6xl mx-auto my-8 rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-stone-800 flex items-center justify-between sticky top-0 bg-stone-900 z-10">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E0651F]">{isNew ? "Nuevo artículo" : "Editar artículo"}</div>
            <h2 className="font-display text-xl font-black text-white truncate">{form.title || "Sin título"}</h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setShowPreview(!showPreview)} className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-bold text-stone-400 hover:text-white px-3 py-2 border border-stone-700 rounded-sm" data-testid="blog-preview-toggle">
              <Eye className="w-3.5 h-3.5" /> {showPreview ? "Ocultar preview" : "Preview"}
            </button>
            <button onClick={onClose} className="text-stone-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {isNew && seoPlan.length > 0 && (
            <div className="bg-[#E0651F]/10 border border-[#E0651F]/30 rounded-sm p-4" data-testid="seo-plan-loader">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#E0651F] mb-2 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Plan editorial SEO
              </div>
              <select
                value={seoPlanSel}
                onChange={(e) => loadFromPlan(e.target.value)}
                className="field"
                data-testid="seo-plan-select"
              >
                <option value="">— Cargar artículo del plan SEO (autocompleta título, categoría, keywords y briefing) —</option>
                {seoPlan.map((p, idx) => {
                  const srcTag = p.source === "radar" ? " 🎯 [GSC Radar]" : p.source === "ai" ? " ✨ [IA]" : "";
                  return (
                    <option
                      key={p.slug}
                      value={p.slug}
                      disabled={p.already_drafted}
                    >
                      {`${idx + 1}. ${p.title}${srcTag}${p.already_drafted ? "  ✓ ya creado" : ""}`}
                    </option>
                  );
                })}
              </select>
              <div className="text-[10px] text-stone-400 mt-2">
                Basado en investigación SEO Feb 2026. Orden = prioridad de publicación. Tras seleccionar, pulsa <b>Generar borrador</b>.
              </div>
              <div className="mt-3 pt-3 border-t border-stone-700 flex flex-wrap items-center gap-2">
                <button type="button" onClick={copyChatGptPrompt} disabled={copyingPrompt}
                  className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-bold rounded-sm"
                  data-testid="blog-copy-chatgpt-prompt-btn"
                  title="Rellena título, categoría y keywords con el siguiente tema del plan, y copia al portapapeles el prompt listo para pegar en ChatGPT">
                  {copyingPrompt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                  {promptCopied ? "✓ Prompt copiado — pega en ChatGPT" : "Copiar prompt para ChatGPT"}
                </button>
                <span className="text-[10px] text-stone-400">
                  Pega la respuesta de ChatGPT (con línea `META: ...` opcional) en el campo <b>Contenido markdown</b>. Cero coste de créditos.
                </span>
              </div>
            </div>
          )}

          <Field label="Título *">
            <input value={form.title} onChange={set("title")} className="field" data-testid="blog-title" />
          </Field>

          {/* IMAGEN DE PORTADA */}
          <div className="bg-stone-800/40 border border-stone-700 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#E0651F] flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> Imagen de portada
              </div>
              <div className="flex items-center gap-2">
                <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="blog-cover-upload-input" />
                <button onClick={() => coverInputRef.current?.click()} disabled={uploadingImg} data-testid="blog-cover-upload-btn"
                  className="inline-flex items-center gap-1.5 bg-stone-700 hover:bg-stone-600 disabled:opacity-50 text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm">
                  {uploadingImg ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Subir
                </button>
                <button onClick={generateCover} disabled={generatingCover || !form.title} data-testid="blog-cover-generate-btn"
                  className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-40 text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm">
                  {generatingCover ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generar IA
                </button>
              </div>
            </div>
            {coverPreviewUrl ? (
              <div className="relative">
                <img src={coverPreviewUrl} alt={form.cover_image_alt || form.title} className="w-full max-h-64 object-cover rounded-sm" data-testid="blog-cover-preview" />
                <button onClick={() => setForm((f) => ({ ...f, cover_image_url: "" }))} className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white p-1.5 rounded-sm" title="Quitar imagen">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-stone-700 rounded-sm p-8 text-center text-stone-500 text-xs">
                Sin imagen. Sube una o genera con IA. Tamaño ideal: 1200×630 px (landscape).
              </div>
            )}
            {coverPreviewUrl && (
              <Field label="ALT text (SEO) — describe la imagen brevemente">
                <input value={form.cover_image_alt} onChange={set("cover_image_alt")} className="field" placeholder="Ej: Portal de subastas BOE en un ordenador con documentos legales" data-testid="blog-cover-alt" />
              </Field>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Categoría *">
              <select value={form.category} onChange={set("category")} className="field">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="CTA final">
              <select value={form.cta_target} onChange={set("cta_target")} className="field">
                {CTAS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Palabras clave (separadas por comas)">
            <input value={form.keywords} onChange={set("keywords")} placeholder="subastas BOE, nota simple, embargo..." className="field" />
          </Field>

          <Field label="Meta description (155 chars max — usado en Google)">
            <input value={form.meta_description} onChange={set("meta_description")} maxLength={170} className="field" placeholder="Resumen de 1-2 frases que Google muestra en los resultados de búsqueda" data-testid="blog-meta-description" />
            <div className="text-[10px] text-stone-500 mt-1">{(form.meta_description || "").length}/170 caracteres</div>
          </Field>

          <Field label="Extracto (aparece en listado del blog)">
            <textarea rows={2} value={form.excerpt} onChange={set("excerpt")} className="field resize-none" />
          </Field>

          {/* AI Draft section */}
          <div className="bg-stone-800/40 border border-stone-700 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#E0651F] flex items-center gap-2"><Sparkles className="w-4 h-4" /> Generar contenido con IA</div>
              <button onClick={generateDraft} disabled={generating || !form.title} className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-40 text-white px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="blog-generate-draft-btn">
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {generating ? "Generando…" : "Generar borrador"}
              </button>
            </div>
            {aiCluster && (
              <div className="text-[11px] text-[#E0651F] mb-2" data-testid="seo-cluster-badge">
                Clúster SEO: <b>{aiCluster}</b>
              </div>
            )}
            <textarea rows={3} placeholder="Notas/ángulo para la IA (opcional): ej. 'enfocar a inversores principiantes con ejemplos en cifras...'"
              value={aiNotes} onChange={(e) => setAiNotes(e.target.value)} className="field resize-none text-xs" />
            <div className="text-[10px] text-stone-500 mt-2">Claude Sonnet 4.5 · Genera 1800-2500 palabras siguiendo el briefing SEO oficial de fmsubastas (intro + TL;DR + casos prácticos + checklist + FAQ). Edita después de generar.</div>
          </div>

          {/* CONTENT EDITOR with toolbar + optional preview */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1.5">Contenido (markdown) *</div>
            {/* Toolbar */}
            <div className="flex items-center flex-wrap gap-1 bg-stone-800 border border-stone-700 border-b-0 rounded-t-sm px-2 py-1.5">
              <ToolbarBtn onClick={() => wrapSelection("**")} title="Negrita" testid="md-bold"><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => wrapSelection("*")} title="Cursiva" testid="md-italic"><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
              <Sep />
              <ToolbarBtn onClick={() => insertLine("## ")} title="Subtítulo H2" testid="md-h2"><Heading2 className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => insertLine("### ")} title="Subtítulo H3" testid="md-h3"><Heading3 className="w-3.5 h-3.5" /></ToolbarBtn>
              <Sep />
              <ToolbarBtn onClick={() => insertLine("- ")} title="Lista" testid="md-list"><List className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => insertLine("> ")} title="Cita" testid="md-quote"><Quote className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => wrapSelection("[", "](https://)")} title="Enlace" testid="md-link"><LinkIcon className="w-3.5 h-3.5" /></ToolbarBtn>
              <Sep />
              <input ref={inlineInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadInline(e.target.files?.[0])} data-testid="blog-inline-upload-input" />
              <ToolbarBtn onClick={() => inlineInputRef.current?.click()} title="Insertar imagen inline" testid="md-image">
                {uploadingImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
              </ToolbarBtn>
              <div className="ml-auto text-[10px] text-stone-500 px-2">
                {form.content_markdown ? `${form.content_markdown.split(/\s+/).filter(Boolean).length} palabras` : ""}
              </div>
            </div>
            <div className={showPreview ? "grid md:grid-cols-2 gap-3" : ""}>
              <textarea ref={textareaRef} rows={20} value={form.content_markdown} onChange={set("content_markdown")} className="field rounded-t-none resize-y font-mono text-xs leading-relaxed" data-testid="blog-content-textarea" />
              {showPreview && (
                <div className="border border-stone-700 border-l-0 md:border-l rounded-sm bg-stone-950 p-5 overflow-y-auto max-h-[600px] prose prose-invert prose-sm" data-testid="blog-preview-pane">
                  <MarkdownPreview md={form.content_markdown} apiBase={API_BASE} />
                </div>
              )}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={set("published")} />
            <span className="text-sm text-stone-200">Publicado (visible públicamente)</span>
          </label>

          {err && <div className="text-red-400 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {err}</div>}
        </div>

        <div className="px-6 py-4 border-t border-stone-800 flex items-center justify-end gap-2 sticky bottom-0 bg-stone-900">
          <button onClick={onClose} className="text-stone-400 hover:text-white text-xs uppercase tracking-[0.15em] font-bold px-3 py-2">Cancelar</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="blog-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Crear" : "Guardar cambios"}
          </button>
        </div>

        <style>{`
          .field {
            width: 100%;
            background: #1c1917;
            border: 1px solid #44403c;
            color: #f5f5f4;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            border-radius: 2px;
            outline: none;
          }
          .field:focus { border-color: #E0651F; }
        `}</style>
      </div>
    </div>
  );
}

function ToolbarBtn({ onClick, title, children, testid }) {
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testid}
      className="w-8 h-8 flex items-center justify-center rounded-sm text-stone-300 hover:bg-stone-700 hover:text-[#E0651F] transition-colors">
      {children}
    </button>
  );
}
function Sep() { return <div className="w-px h-5 bg-stone-700 mx-1" />; }

// Ultra-light markdown preview (no extra dep). Soporta H2/H3, bold/italic,
// listas, citas, imágenes, párrafos.
function MarkdownPreview({ md, apiBase }) {
  if (!md) return <div className="text-stone-600 text-xs">Sin contenido todavía…</div>;
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  let buf = [];
  const flushP = () => {
    if (buf.length) {
      out.push(<p key={`p-${out.length}`} className="text-stone-300 leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: inline(buf.join(" ")) }} />);
      buf = [];
    }
  };
  const inline = (s) => {
    // image
    s = s.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, url) => {
      const full = url.startsWith("http") ? url : `${apiBase}${url}`;
      return `<img src="${full}" alt="${alt}" style="max-width:100%;border-radius:2px;margin:12px 0" />`;
    });
    // link
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" style="color:#E0651F;text-decoration:underline">$1</a>');
    // bold
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong style="color:#fff">$1</strong>');
    // italic
    s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    return s;
  };
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("## ")) {
      flushP();
      out.push(<h2 key={`h2-${i}`} className="font-display text-xl font-black text-white mt-5 mb-2">{line.slice(3)}</h2>);
    } else if (line.startsWith("### ")) {
      flushP();
      out.push(<h3 key={`h3-${i}`} className="font-display text-base font-bold text-[#E0651F] mt-4 mb-2 uppercase tracking-wide">{line.slice(4)}</h3>);
    } else if (line.startsWith("> ")) {
      flushP();
      out.push(<blockquote key={`q-${i}`} className="border-l-4 border-[#E0651F] pl-3 italic text-stone-400 my-3" dangerouslySetInnerHTML={{ __html: inline(line.slice(2)) }} />);
    } else if (line.startsWith("- ")) {
      flushP();
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      out.push(<ul key={`ul-${i}`} className="list-disc list-inside text-stone-300 space-y-1 mb-3">
        {items.map((it, k) => <li key={k} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}
      </ul>);
      continue;
    } else if (!line.trim()) {
      flushP();
    } else {
      buf.push(line);
    }
    i++;
  }
  flushP();
  return <>{out}</>;
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function ImportSeedButton({ onDone }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm("¿Importar los 12 artículos del paquete inicial?\n\nNo se duplicarán artículos que ya existan (por slug).")) return;
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/admin/blog/import-seed");
      setResult({ ok: true, ...data });
      onDone();
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "Error" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <button onClick={run} disabled={running}
        data-testid="blog-import-seed-btn"
        className="inline-flex items-center gap-1.5 bg-stone-800 hover:bg-stone-700 disabled:opacity-50 border border-stone-700 text-stone-200 px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm">
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        Importar 12 artículos
      </button>
      {result && (
        <div className="ml-2 text-xs flex items-center gap-1.5">
          {result.ok ? (
            <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {result.created} creados, {result.skipped} ya existían</span>
          ) : (
            <span className="text-red-400 inline-flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {result.error}</span>
          )}
        </div>
      )}
    </>
  );
}


function AutoGenerateNowButton({ onDone }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState("");

  const run = async () => {
    if (!window.confirm("¿Generar AHORA un borrador del próximo tema programado?\n\nIncluye contenido SEO + imagen de portada IA (~2-5 min). Se ejecuta en segundo plano y quedará como BORRADOR en la lista.")) return;
    setRunning(true); setResult(null); setStep("Iniciando…");
    try {
      // Encola en background para evitar Cloudflare 100s timeout
      const { data: job } = await api.post("/admin/blog/auto-generate-now");
      if (!job?.job_id) throw new Error("Respuesta inesperada del servidor");
      // Poll cada 4s hasta 10 min (150 iteraciones). Producción a veces
      // es más lenta que preview por cold-starts del LLM proxy.
      let consecutiveFails = 0;
      for (let i = 0; i < 150; i++) {
        await new Promise(r => setTimeout(r, 4000));
        try {
          const { data: s } = await api.get(`/admin/blog/auto-jobs/${job.job_id}`);
          consecutiveFails = 0;
          if (s.step) setStep(`${s.step} (${s.progress || 0}%)`);
          if (s.status === "done") {
            setResult({ ok: true, message: s.message || "Borrador generado" });
            onDone();
            return;
          }
          if (s.status === "error") {
            setResult({ ok: false, error: s.error || "Error generando" });
            return;
          }
        } catch {
          consecutiveFails++;
          // Tolerancia amplia: 15 fallos consecutivos = 1 min de red mala.
          // Antes con 6 fallos (24s) cualquier hipo de Cloudflare tumbaba la UX.
          if (consecutiveFails >= 15) {
            setResult({ ok: false, error: "Error de red persistente (>1 min sin respuesta). Refresco la lista por si terminó en background." });
            onDone();
            return;
          }
        }
      }
      setResult({ ok: false, error: "Tiempo agotado (>10 min). El borrador puede haberse creado igualmente — refresco la lista..." });
      // Aún así refrescamos por si el backend terminó en background
      onDone();
    } catch (e) {
      setResult({ ok: false, error: formatApiError(e?.response?.data?.detail) || "Error iniciando el job" });
      onDone();
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <button onClick={run} disabled={running}
        data-testid="blog-auto-generate-now-btn"
        title="Genera automáticamente un borrador con IA (Claude + imagen Gemini) del siguiente tema del plan SEO"
        className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm">
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        Generar con IA
      </button>
      {running && step && (
        <div className="ml-2 text-xs text-amber-400 inline-flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {step}
        </div>
      )}
      {result && (
        <div className="ml-2 text-xs flex items-center gap-1.5">
          {result.ok ? (
            <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {result.message || "Generado"}</span>
          ) : (
            <span className="text-red-400 inline-flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {result.error}</span>
          )}
        </div>
      )}
    </>
  );
}
